/**
 * useOrgTreeLayout Hook (V104)
 *
 * 조직도 레이아웃(노드 위치, 엣지 경로)을 저장/로드하는 Hook입니다.
 *
 * **저장 위치:**
 * Firestore: orgTreeLayouts/{layoutId}
 * 현재는 'default' 단일 레이아웃만 사용
 *
 * **기능:**
 * - 레이아웃 로드 (Firestore에서 조회)
 * - 레이아웃 저장 (Firestore에 저장)
 * - 로딩/에러 상태 관리
 */

import { useState, useEffect, useCallback } from 'react';
import { doc, getDoc, setDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase/config';
import {
  OrgTreeLayout,
  OrgNodePosition,
  OrgEdgePath,
  SaveOrgTreeLayoutInput,
  SavedViewport,
} from '../types/orgTreeLayout';
import { useCurrentUser } from './useCurrentUser';

/**
 * 기본 레이아웃 ID
 */
const DEFAULT_LAYOUT_ID = 'default';

/**
 * 기본 그리드 크기 (px)
 */
export const DEFAULT_GRID_SIZE = 20;

/**
 * useOrgTreeLayout Hook 반환 타입
 */
export interface UseOrgTreeLayoutResult {
  /** 레이아웃 데이터 */
  layout: OrgTreeLayout | null;

  /** 로딩 중 여부 */
  isLoading: boolean;

  /** 저장 중 여부 */
  isSaving: boolean;

  /** 에러 */
  error: Error | null;

  /** 레이아웃 저장 */
  saveLayout: (input: SaveOrgTreeLayoutInput) => Promise<void>;

  /** 레이아웃 새로고침 */
  refetch: () => Promise<void>;
}

/**
 * 조직도 레이아웃 저장/로드 Hook
 *
 * @param layoutId 레이아웃 ID (기본값: 'default')
 */
export function useOrgTreeLayout(
  layoutId: string = DEFAULT_LAYOUT_ID
): UseOrgTreeLayoutResult {
  const [layout, setLayout] = useState<OrgTreeLayout | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [error, setError] = useState<Error | null>(null);

  const { user } = useCurrentUser();

  /**
   * 레이아웃 로드
   */
  const fetchLayout = useCallback(async () => {
    if (!db) {
      setLayout(null);
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const docRef = doc(db, 'orgTreeLayouts', layoutId);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setLayout({
          id: docSnap.id,
          nodes: data.nodes || {},
          edges: data.edges || [],
          gridSize: data.gridSize || DEFAULT_GRID_SIZE,
          // V114: viewport 로드
          viewport: data.viewport || undefined,
          updatedAt: data.updatedAt,
          updatedBy: data.updatedBy || '',
        });
      } else {
        // 레이아웃 문서가 없으면 null (자동 레이아웃 사용)
        setLayout(null);
      }
    } catch (err) {
      console.error('[useOrgTreeLayout] 레이아웃 로드 실패:', err);
      setError(err instanceof Error ? err : new Error('레이아웃 로드 실패'));
    } finally {
      setIsLoading(false);
    }
  }, [layoutId]);

  /**
   * 레이아웃 저장
   */
  const saveLayout = useCallback(
    async (input: SaveOrgTreeLayoutInput) => {
      if (!db) {
        throw new Error('Firestore not initialized');
      }

      if (!user?.profile?.id) {
        throw new Error('로그인이 필요합니다.');
      }

      try {
        setIsSaving(true);
        setError(null);

        // V113.1: 노드 배열 → 맵 변환 (undefined 필드 제거)
        const nodesMap: Record<string, OrgNodePosition> = {};
        input.nodes.forEach((node) => {
          const sanitized: OrgNodePosition = {
            orgUnitId: node.orgUnitId,
            x: node.x,
            y: node.y,
          };
          // Firestore는 undefined 저장 불가 → 조건부 포함
          if (node.width != null) sanitized.width = node.width;
          if (node.height != null) sanitized.height = node.height;
          nodesMap[node.orgUnitId] = sanitized;
        });

        const docRef = doc(db, 'orgTreeLayouts', layoutId);

        // V114: viewport 포함 저장 데이터 구성 (undefined 필드 제거)
        const saveData: Record<string, any> = {
          nodes: nodesMap,
          edges: input.edges,
          gridSize: input.gridSize,
          updatedAt: Timestamp.now(),
          updatedBy: user.profile.id,
        };

        // V114: viewport가 있으면 추가
        if (input.viewport) {
          saveData.viewport = input.viewport;
        }

        // V104.2: merge: true 옵션 추가 - 문서가 없으면 생성, 있으면 병합
        await setDoc(docRef, saveData, { merge: true });

        // 로컬 상태 업데이트
        setLayout({
          id: layoutId,
          nodes: nodesMap,
          edges: input.edges,
          gridSize: input.gridSize,
          // V114: viewport 포함
          viewport: input.viewport,
          updatedAt: Timestamp.now(),
          updatedBy: user.profile.id,
        });

      } catch (err: any) {
        // V104.3: 상세 에러 로깅 및 사용자 친화적 메시지 구분
        const errorCode = err?.code || 'unknown';
        const errorMessage = err?.message || '알 수 없는 오류';

        console.error('[useOrgTreeLayout] 레이아웃 저장 실패:', {
          errorCode,
          errorMessage,
          userId: user?.profile?.id,
          userRoles: user?.roles,
          fullError: err,
        });

        // 에러 코드별 사용자 친화적 메시지
        let userMsg: string;
        if (errorCode === 'permission-denied') {
          userMsg = '권한이 없습니다. 인사팀 또는 Admin(system_admin) 역할이 필요합니다.';
        } else if (errorCode === 'unavailable') {
          userMsg = '서버에 연결할 수 없습니다. 네트워크를 확인해 주세요.';
        } else if (errorCode === 'unauthenticated') {
          userMsg = '로그인 세션이 만료되었습니다. 다시 로그인해 주세요.';
        } else {
          userMsg = `저장 실패: ${errorMessage}`;
        }

        setError(new Error(userMsg));
        throw new Error(userMsg);
      } finally {
        setIsSaving(false);
      }
    },
    [layoutId, user]
  );

  // 초기 로드
  useEffect(() => {
    fetchLayout();
  }, [fetchLayout]);

  return {
    layout,
    isLoading,
    isSaving,
    error,
    saveLayout,
    refetch: fetchLayout,
  };
}
